import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Select,
  Table as ChakraTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  InputGroup,
  InputLeftElement,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import { FiSearch, FiChevronUp, FiChevronDown, FiChevronLeft, FiChevronRight } from "react-icons/fi";

export interface TableColumn<T = any> {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  className?: string;
  render?: (item: T, index: number) => React.ReactNode;
  headerRender?: () => React.ReactNode;
}

export interface TableFilter {
  key: string;
  type: "text" | "select";
  label: string;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  width?: string;
}

export interface TablePagination {
  total: number;
  offset: number;
  has_more: boolean;
}

export interface TableProps<T = any> {
  // Data
  data: T[];
  columns: TableColumn<T>[];
  loading?: boolean;
  error?: string | null;

  // Pagination
  pagination: TablePagination;
  limit?: number;
  onPageChange?: (offset: number) => void;

  // Sorting
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort?: (sortBy: string, sortOrder: "asc" | "desc") => void;

  // Filtering
  filters?: TableFilter[];
  filterValues?: Record<string, string>;
  onFilterChange?: (filters: Record<string, string>) => void;
  onApplyFilters?: () => void;
  onClearFilters?: () => void;

  // Selection
  selectable?: boolean;
  selectedItems?: Set<string>;
  onSelectionChange?: (selectedItems: Set<string>) => void;
  getItemId?: (item: T) => string;
  singleSelection?: boolean;
  hideSelectionCount?: boolean;

  // Row actions
  onRowClick?: (item: T) => void;
  rowActions?: (item: T) => React.ReactNode;

  // Custom header actions
  headerActions?: React.ReactNode;

  // Styling
  className?: string;
  emptyMessage?: string;
  emptySubMessage?: string;
}

const Table = <T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  error = null,
  pagination,
  limit = 50,
  onPageChange,
  sortBy,
  sortOrder = "asc",
  onSort,
  filters = [],
  filterValues: externalFilterValues,
  onFilterChange,
  onApplyFilters,
  onClearFilters,
  selectable = false,
  selectedItems = new Set(),
  onSelectionChange,
  getItemId = (item) => item.id,
  singleSelection = false,
  hideSelectionCount = false,
  onRowClick,
  rowActions,
  headerActions,
  className = "eva-table-container",
  emptyMessage = "No data found",
  emptySubMessage = "Try adjusting your search criteria",
}: TableProps<T>) => {
  const [internalFilterValues, setInternalFilterValues] = useState<Record<string, string>>({});

  // Use external filter values if provided, otherwise use internal state
  const filterValues = externalFilterValues !== undefined ? externalFilterValues : internalFilterValues;

  // Handle filter value changes
  const handleFilterChange = (key: string, value: string) => {
    const newFilterValues = { ...filterValues, [key]: value };

    // Update internal state only if external filter values are not provided
    if (externalFilterValues === undefined) {
      setInternalFilterValues(newFilterValues);
    }

    onFilterChange?.(newFilterValues);
  };

  // Handle selection
  const handleSelectItem = (itemId: string, isSelected: boolean) => {
    if (!onSelectionChange) return;

    if (singleSelection) {
      // For single selection, replace the entire selection with the new item
      if (isSelected) {
        onSelectionChange(new Set([itemId]));
      } else {
        onSelectionChange(new Set());
      }
    } else {
      // For multiple selection, add/remove items as before
      const newSelectedItems = new Set(selectedItems);
      if (isSelected) {
        newSelectedItems.add(itemId);
      } else {
        newSelectedItems.delete(itemId);
      }
      onSelectionChange(newSelectedItems);
    }
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (!onSelectionChange || singleSelection) return;

    if (isSelected) {
      const allItemIds = new Set(data.map(item => getItemId(item)).filter(Boolean));
      onSelectionChange(allItemIds);
    } else {
      onSelectionChange(new Set());
    }
  };

  // Handle sorting
  const handleSort = (columnKey: string) => {
    if (!onSort) return;

    const newSortOrder = sortBy === columnKey && sortOrder === "asc" ? "desc" : "asc";
    onSort(columnKey, newSortOrder);
  };

  // Pagination calculations
  const currentPage = Math.floor((pagination.offset || 0) / limit) + 1;
  const totalPages = Math.ceil((pagination.total || 0) / limit);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const newOffset = (currentPage - 2) * limit;
      onPageChange?.(newOffset);
    }
  };

  const handleNextPage = () => {
    if (pagination.has_more) {
      const newOffset = (pagination.offset || 0) + limit;
      onPageChange?.(newOffset);
    }
  };

  // Render filter header
  const renderFilterHeader = (totalCount: number) => (
    <HStack spacing={3} className="eva-filters-card" w="100%" flexWrap="wrap" align="stretch" justify="space-between">
      {selectable && selectedItems.size > 0 && headerActions && !hideSelectionCount ? (
        // Selection mode header - only show when there are header actions
        <HStack spacing={3} flexWrap="wrap" align="center">
          <Text color="blue.600" fontSize="sm" fontWeight="medium">
            {selectedItems.size} item{selectedItems.size !== 1 ? "s" : ""} selected
          </Text>
          {headerActions}
        </HStack>
      ) : (
        // Normal filter header
        <>
          {selectable && selectedItems.size > 0 && !singleSelection && !hideSelectionCount && (
            <Text color="blue.600" fontSize="sm" fontWeight="medium" mr={4}>
              {selectedItems.size} item{selectedItems.size !== 1 ? "s" : ""} selected
            </Text>
          )}
          <HStack spacing={3} w="100%" flexWrap="wrap" align="stretch">
            {filters.map((filter) => (
              <Box key={filter.key} flex="1 1 220px" minW="160px" maxW={filter.width || "360px"}>
                {filter.type === "text" ? (
                  <InputGroup w="100%">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      size="sm"
                      placeholder={filter.placeholder || `Search ${filter.label.toLowerCase()}...`}
                      value={filterValues[filter.key] || ""}
                      onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                    />
                  </InputGroup>
                ) : (
                  <Select
                    size="sm"
                    value={filterValues[filter.key] || ""}
                    onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                    w="100%"
                  >
                    {filter.options?.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Box>
            ))}

            {onApplyFilters && (
              <Button
                onClick={onApplyFilters}
                colorScheme="blue"
                size="sm"
                flexShrink={0}
              >
                Get Results
              </Button>
            )}

            {onClearFilters && (
              <Button
                onClick={() => {
                  // Reset internal filter values if external filter values are not provided
                  if (externalFilterValues === undefined) {
                    setInternalFilterValues({});
                  }
                  onClearFilters();
                }}
                variant="outline"
                size="sm"
                pr={6}
                pl={6}
                flexShrink={0}
              >
                Clear
              </Button>
            )}
            {/* Total count aligned to the end, stays on same row when space allows */}
            <Box ml="auto" flexShrink={0} alignContent="center">
              <Text color="gray.600" fontSize="sm" mb={0}>
                Total {totalCount} items
              </Text>
            </Box>
          </HStack>
        </>
      )}
    </HStack>
  );

  // Loading state
  if (loading && data.length === 0) {
    return (
      <Box className={className}>
        <VStack align="stretch">
          {renderFilterHeader(0)}
          <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
            <VStack spacing={4}>
              <Spinner size="xl" color="blue.500" />
              <Text>Loading...</Text>
            </VStack>
          </Box>
        </VStack>
      </Box>
    );
  }

  // Empty state
  if (!loading && data.length === 0) {
    return (
      <Box className={className}>
        <VStack align="stretch">
          {renderFilterHeader(0)}
          <Box display="flex" justifyContent="center" alignItems="center" minH="300px" bg="gray.50" borderRadius="md">
            <VStack spacing={4}>
              <Text fontSize="lg" color="gray.500" fontWeight="medium">
                {emptyMessage}
              </Text>
              <Text fontSize="sm" color="gray.400" textAlign="center">
                {emptySubMessage}
              </Text>
            </VStack>
          </Box>
        </VStack>
      </Box>
    );
  }

  return (
    <Box className={className}>
      <VStack align="stretch">
        {/* Filters */}
        {renderFilterHeader(data.length)}

        {/* Error Alert */}
        {error && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            <Box>
              <AlertTitle>Error loading data!</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
          </Alert>
        )}

        {/* Table */}
        <Box className="eva-table-wrapper eva-small-scrollbar">
          <ChakraTable variant="simple">
            <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
              <Tr>
                {selectable && !singleSelection && (
                  <Th className="eva-checkbox-column">
                    <input
                      type="checkbox"
                      checked={data.length > 0 && selectedItems.size === data.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      style={{ cursor: "pointer" }}
                    />
                  </Th>
                )}
                {selectable && singleSelection && (
                  <Th className="eva-checkbox-column" width="50px">
                    {/* Empty header for single selection mode */}
                  </Th>
                )}
                {columns.map((column) => (
                  <Th
                    key={column.key}
                    className={`${column.className || ""} ${column.sortable ? "eva-sortable-header" : ""}`}
                    width={column.width}
                    onClick={column.sortable ? () => handleSort(column.key) : undefined}
                    cursor={column.sortable ? "pointer" : "default"}
                  >
                    {column.headerRender ? (
                      column.headerRender()
                    ) : (
                      <HStack spacing={1}>
                        <Text mb="0">{column.label}</Text>
                        {column.sortable && sortBy === column.key && (
                          sortOrder === "asc" ? <FiChevronUp /> : <FiChevronDown />
                        )}
                      </HStack>
                    )}
                  </Th>
                ))}
                {rowActions && <Th className="eva-actions-column">Actions</Th>}
              </Tr>
            </Thead>
            <Tbody>
              {data.map((item, index) => {
                const itemId = getItemId(item);
                return (
                  <Tr
                    key={itemId}
                    _hover={{ bg: "gray.50" }}
                    cursor={onRowClick ? "pointer" : "default"}
                    onClick={() => onRowClick?.(item)}
                  >
                    {selectable && (
                      <Td className="eva-checkbox-column" onClick={(e) => e.stopPropagation()}>
                        <input
                          type={singleSelection ? "radio" : "checkbox"}
                          name={singleSelection ? "single-selection" : undefined}
                          checked={itemId ? selectedItems.has(itemId) : false}
                          onChange={(e) => itemId && handleSelectItem(itemId, e.target.checked)}
                          style={{ cursor: "pointer" }}
                        />
                      </Td>
                    )}
                    {columns.map((column) => (
                      <Td key={column.key} className={column.className}>
                        {column.render ? column.render(item, index) : item[column.key]}
                      </Td>
                    ))}
                    {rowActions && (
                      <Td className="eva-actions-column" onClick={(e) => e.stopPropagation()}>
                        {rowActions(item)}
                      </Td>
                    )}
                  </Tr>
                );
              })}
            </Tbody>
          </ChakraTable>
        </Box>

        {/* Pagination Controls */}
        {onPageChange && (
          <HStack justify="center" p={4} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
            <Button
              size="sm"
              onClick={handlePreviousPage}
              isDisabled={currentPage <= 1}
              variant="outline"
            >
              <FiChevronLeft />
            </Button>
            <Text fontSize="sm" color="gray.600" mb={0}>
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              size="sm"
              onClick={handleNextPage}
              isDisabled={!pagination.has_more}
              variant="outline"
            >
              <FiChevronRight />
            </Button>
          </HStack>
        )}
      </VStack>
    </Box>
  );
};

export default Table;
